// src/services/reminder.service.js
import { db } from "@/firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { Logger } from "@/utils/logger";
import {
  NotificationService,
  NOTIFICATION_TYPES,
} from "@/services/notification.service";

// firestore.rules scopes scheduledReminders to their participants, and
// Firestore denies any query that could match someone else's documents. Every
// lesson-scoped query must therefore also filter on the current user's uid;
// since the caller may be the student or the teacher, run both filters and
// merge the (de-duplicated) results.
const ownerScopedReminderDocs = async (...constraints) => {
  const { getCurrentUser } = await import("@/services/auth.service");
  const uid = getCurrentUser()?.uid;
  if (!uid) return [];
  const remindersRef = collection(db, "scheduledReminders");
  const [asStudent, asTeacher] = await Promise.all([
    getDocs(query(remindersRef, where("studentId", "==", uid), ...constraints)),
    getDocs(query(remindersRef, where("teacherId", "==", uid), ...constraints)),
  ]);
  const seen = new Set();
  const docs = [];
  for (const snap of [asStudent, asTeacher]) {
    snap.forEach((doc) => {
      if (!seen.has(doc.id)) {
        seen.add(doc.id);
        docs.push(doc);
      }
    });
  }
  return docs;
};

/**
 * Reminder types and their timing
 */
export const REMINDER_TYPES = {
  LESSON_24H: {
    type: NOTIFICATION_TYPES.LESSON_REMINDER_24H,
    hoursBeforeLesson: 24,
    description: "24 hours before lesson",
  },
  LESSON_2H: {
    type: NOTIFICATION_TYPES.LESSON_REMINDER_2H,
    hoursBeforeLesson: 2,
    description: "2 hours before lesson",
  },
  LESSON_30M: {
    type: NOTIFICATION_TYPES.LESSON_REMINDER_30M,
    hoursBeforeLesson: 0.5,
    description: "30 minutes before lesson",
  },
};

/**
 * Reminder status constants
 */
export const REMINDER_STATUS = {
  SCHEDULED: "scheduled",
  SENT: "sent",
  CANCELLED: "cancelled",
  FAILED: "failed",
};

export class ReminderService {
  /**
   * Schedule all reminders for a lesson
   * @param {Object} lessonData - The lesson data
   * @returns {Promise<Array>} - Array of scheduled reminder IDs
   */
  static async scheduleRemindersForLesson(lessonData) {
    try {
      Logger.log(
        "info",
        "Scheduling reminders for lesson",
        { lessonId: lessonData.id, startTime: lessonData.startTime },
        "ReminderService"
      );

      const lessonStartTime = new Date(lessonData.startTime);
      const reminders = [];

      // Schedule each type of reminder
      for (const reminderConfig of Object.values(REMINDER_TYPES)) {
        try {
          const reminderTime = new Date(lessonStartTime);
          reminderTime.setHours(
            reminderTime.getHours() - reminderConfig.hoursBeforeLesson
          );

          // Only schedule if reminder time is in the future
          if (reminderTime > new Date()) {
            const reminderId = await this.scheduleReminder({
              lessonId: lessonData.id,
              studentId: lessonData.studentId,
              teacherId: lessonData.teacherId,
              reminderType: reminderConfig.type,
              scheduledFor: reminderTime,
              lessonData: lessonData,
            });

            reminders.push({
              id: reminderId,
              type: reminderConfig.type,
              scheduledFor: reminderTime,
            });

            Logger.log(
              "info",
              `Scheduled ${reminderConfig.description} reminder`,
              {
                lessonId: lessonData.id,
                reminderId,
                scheduledFor: reminderTime,
              },
              "ReminderService"
            );
          } else {
            Logger.log(
              "info",
              `Skipped ${reminderConfig.description} reminder (lesson too soon)`,
              { lessonId: lessonData.id, reminderTime, lessonStartTime },
              "ReminderService"
            );
          }
        } catch (reminderError) {
          Logger.log(
            "error",
            `Failed to schedule ${reminderConfig.description} reminder`,
            {
              lessonId: lessonData.id,
              error: reminderError.message,
            },
            "ReminderService"
          );
        }
      }

      Logger.log(
        "info",
        "Successfully scheduled reminders for lesson",
        { lessonId: lessonData.id, reminderCount: reminders.length },
        "ReminderService"
      );

      return reminders;
    } catch (error) {
      Logger.log(
        "error",
        "Error scheduling reminders for lesson",
        {
          lessonId: lessonData.id,
          error: error.message,
        },
        "ReminderService"
      );
      throw error;
    }
  }

  /**
   * Schedule a single reminder
   * @param {Object} reminderData - The reminder data
   * @returns {Promise<string>} - The reminder ID
   */
  static async scheduleReminder(reminderData) {
    try {
      const reminder = {
        lessonId: reminderData.lessonId,
        studentId: reminderData.studentId,
        teacherId: reminderData.teacherId,
        reminderType: reminderData.reminderType,
        scheduledFor: reminderData.scheduledFor,
        status: REMINDER_STATUS.SCHEDULED,
        lessonData: reminderData.lessonData,
        attempts: 0,
        createdAt: serverTimestamp(),
      };

      const reminderRef = await addDoc(
        collection(db, "scheduledReminders"),
        reminder
      );

      Logger.log(
        "info",
        "Reminder scheduled successfully",
        {
          reminderId: reminderRef.id,
          lessonId: reminderData.lessonId,
          reminderType: reminderData.reminderType,
          scheduledFor: reminderData.scheduledFor,
        },
        "ReminderService"
      );

      return reminderRef.id;
    } catch (error) {
      Logger.log(
        "error",
        "Error scheduling reminder",
        {
          lessonId: reminderData.lessonId,
          reminderType: reminderData.reminderType,
          error: error.message,
        },
        "ReminderService"
      );
      throw error;
    }
  }

  /**
   * Cancel all reminders for a lesson
   * @param {string} lessonId - The lesson ID
   * @returns {Promise<number>} - Number of reminders cancelled
   */
  static async cancelRemindersForLesson(lessonId) {
    try {
      Logger.log(
        "info",
        "Cancelling reminders for lesson",
        { lessonId },
        "ReminderService"
      );

      const reminderDocs = await ownerScopedReminderDocs(
        where("lessonId", "==", lessonId),
        where("status", "==", REMINDER_STATUS.SCHEDULED)
      );

      let cancelledCount = 0;

      const cancelPromises = reminderDocs.map(async (doc) => {
        try {
          await updateDoc(doc.ref, {
            status: REMINDER_STATUS.CANCELLED,
            cancelledAt: serverTimestamp(),
          });
          cancelledCount++;
        } catch (error) {
          Logger.log(
            "error",
            "Error cancelling reminder",
            {
              reminderId: doc.id,
              lessonId,
              error: error.message,
            },
            "ReminderService"
          );
        }
      });

      await Promise.all(cancelPromises);

      Logger.log(
        "info",
        "Successfully cancelled reminders for lesson",
        { lessonId, cancelledCount },
        "ReminderService"
      );

      return cancelledCount;
    } catch (error) {
      Logger.log(
        "error",
        "Error cancelling reminders for lesson",
        {
          lessonId,
          error: error.message,
        },
        "ReminderService"
      );
      throw error;
    }
  }

  /**
   * Process due reminders (called by scheduled function)
   * @param {Date} currentTime - Current time to check against
   * @returns {Promise<Object>} - Processing results
   */
  static async processDueReminders(currentTime = new Date()) {
    try {
      Logger.log(
        "info",
        "Processing due reminders",
        { currentTime },
        "ReminderService"
      );

      const q = query(
        collection(db, "scheduledReminders"),
        where("status", "==", REMINDER_STATUS.SCHEDULED),
        where("scheduledFor", "<=", currentTime),
        orderBy("scheduledFor")
      );

      const querySnapshot = await getDocs(q);
      const results = {
        processed: 0,
        sent: 0,
        failed: 0,
        errors: [],
      };

      const processingPromises = querySnapshot.docs.map(async (doc) => {
        const reminderData = doc.data();
        results.processed++;

        try {
          // Send the reminder notification
          await NotificationService.sendNotification(
            reminderData.studentId,
            reminderData.reminderType,
            {
              lessonId: reminderData.lessonId,
              lessonData: reminderData.lessonData,
              reminderType: reminderData.reminderType,
              scheduledFor: reminderData.scheduledFor,
              sentAt: currentTime,
            }
          );

          // Mark reminder as sent
          await updateDoc(doc.ref, {
            status: REMINDER_STATUS.SENT,
            sentAt: serverTimestamp(),
          });

          results.sent++;

          Logger.log(
            "info",
            "Reminder sent successfully",
            {
              reminderId: doc.id,
              lessonId: reminderData.lessonId,
              reminderType: reminderData.reminderType,
              studentId: reminderData.studentId,
            },
            "ReminderService"
          );
        } catch (error) {
          // Mark reminder as failed and increment attempts
          const newAttempts = (reminderData.attempts || 0) + 1;
          await updateDoc(doc.ref, {
            status: REMINDER_STATUS.FAILED,
            attempts: newAttempts,
            lastError: error.message,
            lastAttemptAt: serverTimestamp(),
          });

          results.failed++;
          results.errors.push({
            reminderId: doc.id,
            lessonId: reminderData.lessonId,
            error: error.message,
          });

          Logger.log(
            "error",
            "Failed to send reminder",
            {
              reminderId: doc.id,
              lessonId: reminderData.lessonId,
              reminderType: reminderData.reminderType,
              attempts: newAttempts,
              error: error.message,
            },
            "ReminderService"
          );
        }
      });

      await Promise.all(processingPromises);

      Logger.log(
        "info",
        "Completed processing due reminders",
        results,
        "ReminderService"
      );

      return results;
    } catch (error) {
      Logger.log(
        "error",
        "Error processing due reminders",
        {
          currentTime,
          error: error.message,
        },
        "ReminderService"
      );
      throw error;
    }
  }

  /**
   * Reschedule reminders for a lesson (when lesson time changes)
   * @param {string} lessonId - The lesson ID
   * @param {Object} updatedLessonData - Updated lesson data
   * @returns {Promise<Array>} - New reminder IDs
   */
  static async rescheduleRemindersForLesson(lessonId, updatedLessonData) {
    try {
      Logger.log(
        "info",
        "Rescheduling reminders for lesson",
        { lessonId, newStartTime: updatedLessonData.startTime },
        "ReminderService"
      );

      // Cancel existing reminders
      await this.cancelRemindersForLesson(lessonId);

      // Schedule new reminders
      const newReminders = await this.scheduleRemindersForLesson(
        updatedLessonData
      );

      Logger.log(
        "info",
        "Successfully rescheduled reminders for lesson",
        { lessonId, newReminderCount: newReminders.length },
        "ReminderService"
      );

      return newReminders;
    } catch (error) {
      Logger.log(
        "error",
        "Error rescheduling reminders for lesson",
        {
          lessonId,
          error: error.message,
        },
        "ReminderService"
      );
      throw error;
    }
  }

  /**
   * Get scheduled reminders for a lesson
   * @param {string} lessonId - The lesson ID
   * @returns {Promise<Array>} - Scheduled reminders
   */
  static async getRemindersForLesson(lessonId) {
    try {
      const reminderDocs = await ownerScopedReminderDocs(
        where("lessonId", "==", lessonId),
        orderBy("scheduledFor")
      );

      const reminders = [];

      reminderDocs.forEach((doc) => {
        reminders.push({
          id: doc.id,
          ...doc.data(),
          scheduledFor: doc.data().scheduledFor?.toDate(),
          createdAt: doc.data().createdAt?.toDate(),
          sentAt: doc.data().sentAt?.toDate(),
          cancelledAt: doc.data().cancelledAt?.toDate(),
        });
      });

      return reminders;
    } catch (error) {
      Logger.log(
        "error",
        "Error getting reminders for lesson",
        {
          lessonId,
          error: error.message,
        },
        "ReminderService"
      );
      throw error;
    }
  }

  /**
   * Clean up old reminders (for maintenance)
   * @param {number} daysOld - Remove reminders older than this many days
   * @returns {Promise<number>} - Number of reminders cleaned up
   */
  static async cleanupOldReminders(daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const q = query(
        collection(db, "scheduledReminders"),
        where("createdAt", "<", cutoffDate)
      );

      const querySnapshot = await getDocs(q);
      let cleanedCount = 0;

      const deletePromises = querySnapshot.docs.map(async (doc) => {
        try {
          await deleteDoc(doc.ref);
          cleanedCount++;
        } catch (error) {
          Logger.log(
            "error",
            "Error deleting old reminder",
            {
              reminderId: doc.id,
              error: error.message,
            },
            "ReminderService"
          );
        }
      });

      await Promise.all(deletePromises);

      Logger.log(
        "info",
        "Cleaned up old reminders",
        { cleanedCount, cutoffDate },
        "ReminderService"
      );

      return cleanedCount;
    } catch (error) {
      Logger.log(
        "error",
        "Error cleaning up old reminders",
        {
          daysOld,
          error: error.message,
        },
        "ReminderService"
      );
      throw error;
    }
  }
}

export default ReminderService;
